/**
 * Logs — Logs & Audit
 * 2 tabs: Logs système (notifications) + Audit trail
 * Filtres: recherche texte, niveau, catégorie
 */

import { useState, useEffect, useMemo } from "react";
import { api } from "../lib/api";

type LogLevel = "info" | "warn" | "error" | "success" | "debug";
type Severity = "info" | "low" | "medium" | "high" | "critical";

interface AuditEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  actor: string;
  actorType?: string;
  action: string;
  category: string;
  severity: Severity;
  detail?: string;
  resource?: string;
  metadata?: Record<string, unknown> | null;
}

interface NotificationEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  actor: string;
  action: string;
  category: string;
  detail?: string;
}

type LogEntry = AuditEntry | NotificationEntry;

const LEVEL_COLORS: Record<LogLevel, string> = {
  info: "var(--blue)",
  warn: "var(--amber)",
  error: "var(--rust)",
  success: "var(--olive)",
  debug: "var(--muted)",
};

const SEVERITY_COLORS: Record<Severity, string> = {
  info: "var(--blue)",
  low: "var(--olive)",
  medium: "var(--amber)",
  high: "var(--rust)",
  critical: "var(--rust)",
};

const CATEGORIES = [
  "auth",
  "mission",
  "crew",
  "project",
  "system",
  "security",
  "deploy",
  "config",
];

const LEVELS: LogLevel[] = ["info", "warn", "error", "success", "debug"];

export default function Logs() {
  const [tab, setTab] = useState<"system" | "audit">("system");
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [notifications, setNotifications] = useState<NotificationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const load = async () => {
    try {
      setLoading(true);
      const [auditData, notifData] = await Promise.all([
        api.audit.list(),
        api.notifications.list(),
      ]);
      setAudit((auditData ?? []) as unknown as AuditEntry[]);
      setNotifications((notifData ?? []) as unknown as NotificationEntry[]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const entries: LogEntry[] = tab === "audit" ? audit : notifications;

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (levelFilter !== "all" && e.level !== levelFilter) return false;
      if (categoryFilter !== "all" && e.category !== categoryFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const haystack = `${e.actor} ${e.action} ${e.category} ${e.detail ?? ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [entries, search, levelFilter, categoryFilter]);

  const counts = {
    total: entries.length,
    error: entries.filter((e) => e.level === "error").length,
    warn: entries.filter((e) => e.level === "warn").length,
    critical: tab === "audit" ? (audit as AuditEntry[]).filter((e) => (e as AuditEntry).severity === "critical").length : 0,
  };

  return (
    <div className="page active" style={{ display: "grid", gap: "14px" }}>
      <div className="page-header">
        <div>
          <div className="eyebrow">observabilité</div>
          <h1>Logs & Audit</h1>
          <p>
            {counts.total} entrée{counts.total > 1 ? "s" : ""} ·{" "}
            <span style={{ color: "var(--rust)" }}>{counts.error} erreur{counts.error > 1 ? "s" : ""}</span> ·{" "}
            <span style={{ color: "var(--amber)" }}>{counts.warn} warning{counts.warn > 1 ? "s" : ""}</span>
          </p>
        </div>
        <div className="header-actions">
          <button className="btn" onClick={load}>↻ Actualiser</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="view-tabs">
        <span
          className={`view-tab ${tab === "system" ? "active" : ""}`}
          onClick={() => setTab("system")}
        >
          📋 Logs système
          <span className="tab-count">{notifications.length}</span>
        </span>
        <span
          className={`view-tab ${tab === "audit" ? "active" : ""}`}
          onClick={() => setTab("audit")}
        >
          🔍 Audit trail
          <span className="tab-count">{audit.length}</span>
        </span>
      </div>

      {error && (
        <div className="ops-card" style={{ borderColor: "var(--rust)" }}>
          <p style={{ color: "var(--rust)" }}>⚠ {error}</p>
          <button className="btn" onClick={load}>Réessayer</button>
        </div>
      )}

      {loading && <div className="no-results">Chargement des logs…</div>}

      {/* Stats */}
      {!loading && !error && (
        <div className="stats-row" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          <div className="stat-card">
            <div className="eyebrow">total</div>
            <div className="val">{counts.total}</div>
            <div className="sub">entrées</div>
          </div>
          <div className="stat-card">
            <div className="eyebrow">erreurs</div>
            <div className="val" style={{ color: "var(--rust)" }}>{counts.error}</div>
            <div className="sub">niveau error</div>
          </div>
          <div className="stat-card">
            <div className="eyebrow">warnings</div>
            <div className="val" style={{ color: "var(--amber)" }}>{counts.warn}</div>
            <div className="sub">niveau warn</div>
          </div>
          {tab === "audit" && (
            <div className="stat-card">
              <div className="eyebrow">critique</div>
              <div className="val" style={{ color: "var(--rust)" }}>{counts.critical}</div>
              <div className="sub">sévérité critique</div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      {!loading && !error && (
        <div className="ops-card">
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "12px" }}>
            <div className="form-group">
              <label>Recherche</label>
              <input
                className="form-input"
                placeholder="Rechercher dans les logs…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Niveau</label>
              <select
                className="form-select"
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
              >
                <option value="all">Tous les niveaux</option>
                {LEVELS.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Catégorie</label>
              <select
                className="form-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">Toutes les catégories</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <div className="ops-card" style={{ padding: 0, overflow: "hidden" }}>
          {filtered.length === 0 ? (
            <div className="no-results">Aucune entrée ne correspond aux filtres.</div>
          ) : (
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Niveau</th>
                  <th>Acteur</th>
                  <th>Action</th>
                  <th>Catégorie</th>
                  {tab === "audit" && <th>Sévérité</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id}>
                    <td style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--muted)", whiteSpace: "nowrap" }}>
                      {new Date(e.timestamp).toLocaleString("fr-FR")}
                    </td>
                    <td>
                      <span
                        className="log-level"
                        style={{ color: LEVEL_COLORS[e.level], fontWeight: 600 }}
                      >
                        {e.level}
                      </span>
                    </td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: "12px" }}>{e.actor}</td>
                    <td style={{ fontSize: "12px" }}>
                      <div>{e.action}</div>
                      {/* Détail du changement (oldValue → newValue) */}
                      {tab === "audit" && (e as AuditEntry).metadata && (
                        <div style={{ fontSize: "11px", color: "var(--muted)", fontFamily: "var(--mono)", marginTop: "2px" }}>
                          {(() => {
                            const meta = (e as AuditEntry).metadata ?? {};
                            const change = meta.change as string | undefined;
                            if (change) return `↳ ${change}`;
                            const ov = meta.oldValue as string | undefined;
                            const nv = meta.newValue as string | undefined;
                            if (ov !== undefined || nv !== undefined) {
                              return `↳ ${ov ?? "∅"} → ${nv ?? "∅"}`;
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="tag" style={{ fontFamily: "var(--mono)", fontSize: "10px", padding: "3px 8px", borderRadius: "999px", background: "rgba(255,255,255,0.06)" }}>
                        {e.category}
                      </span>
                    </td>
                    {tab === "audit" && (
                      <td>
                        <span
                          className="log-level"
                          style={{ color: SEVERITY_COLORS[(e as AuditEntry).severity], fontWeight: 600 }}
                        >
                          {(e as AuditEntry).severity}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
